import PropTypes from 'prop-types'
import React, { Fragment, memo, useEffect, useState } from 'react'

import { useDropzone } from 'react-dropzone'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import FeedbackTwoToneIcon from '@mui/icons-material/FeedbackTwoTone';

// import { reduxBatch } from '@manaflair/redux-batch'
import { useFormik } from 'formik'
import { useDispatch, useStore } from 'react-redux'

import { Button, FormControl, FormGroup, FormLabel, Input, Modal } from '../../../components'

import { feedbackAction, sendMsgAction } from '../../../utils/actions'

import _set from 'lodash/set'
import { $submitFormToSmartSheet } from '../../../utils/apis'
import { getDownloadContent, stringifyContentWithHash, toJSONFile } from '../../../utils/fns'
import { useModal } from '../../../utils/hooks'
import { getCalcInputState, getCalcOutputState } from '../../../utils/selectors'
import { FeedbackSchema } from '../../../utils/validation'

const FeedbackButton = memo(({ disabled, onClick }) => {
  return (
    <Button
      variant="topNav"
      className="button topNav__button -feedback"
      onClick={onClick}
      disabled={disabled}
    >
      <FeedbackTwoToneIcon className="material-icons-two-tone material-icons-two-tone--noinvert" />
      <span className="button__text">Feedback</span>
    </Button>
  )
})

FeedbackButton.displayName = 'FeedbackButton'

FeedbackButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
}

function formatBytes(bytes, decimals = 0) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const FeedbackModal = ({ appConfig, show, onHide }) => {
  const dispatch = useDispatch()
  const [attachmentsFileError, setAttachmentsFileError] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [showRecipients, setShowRecipients] = useState(false)

  const store = useStore()

  const checkTotalSize = () => {
    const totalSize = attachments.reduce((acc, cur) => {
      return acc + cur.size
    }, 0)

    setAttachmentsFileError(totalSize > 10485760)
  }

  const onDrop = (acceptedFiles) => {
    let myFile = {}
    const reader = new FileReader()

    reader.onload = () => {
      myFile.result = reader.result
      setAttachments([myFile].concat(attachments))
    }

    acceptedFiles.forEach((file) => {
      myFile = {}
      myFile.path = file.path
      myFile.type = file.type
      myFile.size = file.size
      if (
        file.type.indexOf('image') > -1 ||
        file.type.indexOf('video') > -1 ||
        file.type.indexOf('pdf') > -1
      ) {
        reader.readAsArrayBuffer(file)
      } else {
        reader.readAsBinaryString(file)
      }
    })
  }

  const handleRemoveFile = (target) => {
    const attachmentsClone = attachments.filter((item) => item.path !== target)
    setAttachments(attachmentsClone)
  }

  const { executeRecaptcha } = useGoogleReCaptcha()
  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const submitFeedback = async (data, formikHelpers) => {
    const payload = new FormData()
    const token = await executeRecaptcha('')

    const state = store.getState()
    const inputState = getCalcInputState(state)
    const outputState = getCalcOutputState(state)
    const downloadContent = getDownloadContent(inputState, outputState, appConfig)

    if (appConfig.appCode === 'HS' && inputState.jobs) {
      _set(downloadContent, 'calculation.output.jobs', inputState.jobs)
    }

    const stringifiedContent = stringifyContentWithHash(downloadContent)

    const files = toJSONFile(stringifiedContent, `${appConfig.appCode}-DATA`)

    payload.append('files', files)
    payload.append(
      'columns',
      JSON.stringify({
        email: data.email,
        summary: data.summary,
        description: data.description,
      }),
    )

    attachments.forEach((file) => {
      const blob = new Blob([file.result], { type: file.type })
      payload.append('files', blob, file.path)
    })

    if (showRecipients) {
      payload.append('emails', data.email)
    }

    payload.append('g-recaptcha-response', token)

    // payload.append('debug', true)

    onHide()

    await $submitFormToSmartSheet(payload, appConfig.appCode)
      .then(() => {
        dispatch(sendMsgAction({ description: 'Your feedback has been submitted. Thank you.' }))
      })
      .catch(() => {
        // reduxBatch([
        //   dispatch(
        //     feedbackAction('Feedback - message:Smartsheet cancelled. Please try again later.'),
        //   ),
        //   dispatch(sendMsgAction({ msgBody: 'Feedback form error. Please try again later.' })),
        // ])
        dispatch(sendMsgAction({ description: 'Feedback form error. Please try again later.' }))
      })
      .finally(() => {
        setAttachments([])
        formikHelpers.setSubmitting(false)
        formikHelpers.resetForm()
      })
  }

  const cancelFeedback = () => {
    feedbackAction('Feedback - message:Smartsheet cancelled. Please try again later.')
    dispatch(sendMsgAction({ description: 'Feedback cancelled' }))
    onHide()
  }

  useEffect(() => {
    checkTotalSize()
  }, [attachments])

  const formik = useFormik({
    initialValues: {
      email: '',
      description: '',
      summary: '',
    },
    validationSchema: FeedbackSchema,
    onSubmit: submitFeedback,
  })

  return (
    <Modal
      header={
        <Modal.Header closeButton>
          <h3>Issue or Enhancement Submittal</h3>
        </Modal.Header>
      }
      show={show}
      onHide={cancelFeedback}
      footer={
        <Modal.Footer>
          <Fragment>
            <Button variant="tertiary" onClick={cancelFeedback}>
              <span className="material-icons">close</span>
              <span className="button__text">Cancel</span>
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={
                !formik.isValid || !formik.dirty || formik.isSubmitting || attachmentsFileError
              }
              form="feedbackForm"
            >
              <span className="material-icons">send</span>
              <span className="button__text">Submit</span>
            </Button>
          </Fragment>
        </Modal.Footer>
      }
    >
      <p>
        Use this form to report any issues that you&#39;ve found in this application, or to suggest
        enhancements. Your feedback is very important to help us continually improve. Thank you!
      </p>
      <form id="feedbackForm" onSubmit={formik.handleSubmit}>
        <div className="row mb-4">
          <div className="col">
            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormControl
                name="email"
                placeholder="your@email.com"
                {...formik.getFieldProps('email')}
              />
              {formik.touched.email && formik.errors.email && (
                <span className="input-error is-visible">{formik.errors.email}</span>
              )}
            </FormGroup>
          </div>

          <div className="col">
            <FormGroup>
              <FormLabel
                htmlFor="summary"
                popperText={'A brief description of the issue or enhancement'}
              >
                Summary
              </FormLabel>
              <FormControl
                name="summary"
                placeholder="Summary of issue or enhancement"
                {...formik.getFieldProps('summary')}
              />
              {formik.touched.summary && formik.errors.summary && (
                <span className="input-error is-visible">{formik.errors.summary}</span>
              )}
            </FormGroup>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <FormGroup>
              <FormLabel
                htmlFor="description"
                popperText="Describe the issue you are experiencing and the steps necessary to reproduce in
                as much detail as possible. For enhancements, describe functionality that you
                would like to see in the application and why. If you prefer, we can reach out to
                you so that you can demonstrate the issue/feature. Include a phone number here
                if you prefer a phone call, otherwise, we will send you an email."
              >
                Description
              </FormLabel>
              <Input
                name="description"
                as="textarea"
                placeholder="Describe the issue you are experiencing or the enhancement you would like to see"
                {...formik.getFieldProps('description')}
              />
              {formik.touched.description && formik.errors.description && (
                <span className="input-error is-visible">{formik.errors.description}</span>
              )}
            </FormGroup>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <FormGroup style={{ marginTop: '10px' }}>
              <FormLabel
                popperText="Upload videos of the issue, screen shots, or other reference files from the
                software."
              >
                Attach Screenshot or Markups
              </FormLabel>
              {attachments.length > 0 ? (
                <ul className="-noStyle">
                  {attachments.map((file, index) => (
                    <li key={index}>
                      <Button variant="icon -sm" onClick={() => handleRemoveFile(file.path)}>
                        <span className="material-icons">close</span>
                      </Button>{' '}
                      &nbsp;
                      {file.path} - {formatBytes(file.size)}
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="fileUploadZone" {...getRootProps()}>
                <span>
                  <span>Drag and drop a file here or </span>
                  <button type="button" className="button -link">
                    <span className="button__text">browse files</span>
                  </button>
                </span>
                <input {...getInputProps({ multiple: false })} />
              </div>
            </FormGroup>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <FormGroup>
              <input
                id="showRecipients"
                type="checkbox"
                checked={showRecipients}
                onChange={() => setShowRecipients(!showRecipients)}
              />
              <label htmlFor="showRecipients">Send me a copy of my responses</label>
            </FormGroup>
          </div>
        </div>
        {attachmentsFileError ? (
          <div className="notice -error">
            <img
              data-src="assets/images/icons/sst-icon-info.svg"
              className="notice__icon iconic iconic-md injectSVG"
              alt="info"
            />
            <div className="notice__text">
              <div className="notice__header">Maximum File Upload Size Exceeded</div>
              <div className="notice__body">
                The maximum total file upload size is 10 MB. Please remove or update your files to
                not exceed this threshold.
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </Modal>
  )
}

FeedbackModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
}

const GlobalFeedback = ({ appConfig, disabled }) => {
  const [show, onHide] = useModal(false)

  return (
    <Fragment>
      <FeedbackButton onClick={onHide} disabled={disabled} />

      <FeedbackModal show={show} onHide={onHide} appConfig={appConfig} />
    </Fragment>
  )
}

GlobalFeedback.propTypes = {
  disabled: PropTypes.bool,
  appConfig: PropTypes.object.isRequired,
}

export default Object.assign(GlobalFeedback, {
  Button: FeedbackButton,
  Modal: FeedbackModal,
})
